import React, { FC, useState } from 'react'
import { Grid } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { Theme } from '@mui/material/styles'

import { TRANSLATIONS as T } from '../../../constants/translations-constants'
import { CommonPageProps } from '../../../types/interface/fsm.interface'
import { LDContainerV2FSM, LMBasicV3FSM, Typography, withHidden } from '@dg-shared'
import { useTranslate } from '../../../hooks/shared'
import useReactQueryInvoice from '../../../hooks/shared/useReactQueryInvoice'
import { Events } from '../../../FSM/shared/constants'
import { ProductName } from '../../../types/interface/quote.interface'
import { getProductName, formatNorwegianAccountNumber, normalizePaymentMethod } from '@dg-util'
import ProductSelect from '@dg-shared/ProductSelect'
import InvoiceCard, { InvoiceCardData } from '../../../components/shared/InvoiceCard'
import Loading from '../../qb/Loading/Page'
import { InvoiceV2DetailedType } from '../../../types/ApiV2'

const MobileScreen = withHidden(LMBasicV3FSM, true)
const DesktopScreen = withHidden(LDContainerV2FSM, false)

const getInvoiceTitle = (invoice: InvoiceV2DetailedType): string => {
  if (invoice.vehicleName) return `${invoice.vehicleName} – ${invoice.licensePlate}`
  return invoice.agreementDescription
}

const toInvoiceCardData = (invoice: InvoiceV2DetailedType): InvoiceCardData => {
  // DFE-4118: only show a value that is a complete 11-digit Norwegian
  // account number — anything else (empty, malformed) is hidden rather
  // than shown unformatted.
  const isValidAccountNumber = (invoice.accountNumber?.replace(/\D/g, '').length ?? 0) === 11

  return {
    id: String(invoice.invoiceId),
    dueDate: invoice.dueDate,
    title: getInvoiceTitle(invoice),
    subtitle: '',
    dateRange: `${invoice.periodFrom} – ${invoice.periodTo}`,
    amount: invoice.amountNok,
    accountNumber: isValidAccountNumber
      ? formatNorwegianAccountNumber(invoice.accountNumber as string)
      : undefined,
    kid: invoice.kidNumber || undefined,
    paymentMethod: invoice.paymentMethodText
      ? normalizePaymentMethod(invoice.paymentMethodText)
      : undefined,
  }
}

export const useStyles = makeStyles()((theme: Theme) => ({
  title: {
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
  },

  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },

  sectionTitle: {
    fontWeight: 700,
  },

  grid: {
    marginBottom: theme.spacing(3),
    // v4 Grid padded items on all sides; v5 drops the bottom half of the spacing.
    paddingBottom: theme.spacing(1),
  },

  emptyState: {
    textAlign: 'center',
    margin: '48px 0',
  },
}))

const PageInvoicesList: FC<CommonPageProps> = ({ service }) => {
  const { t } = useTranslate(T.INVOICES)
  const { classes } = useStyles()
  const [productFilter, setProductFilter] = useState<ProductName>(getProductName())
  const { unpaidInvoices, availableProductTypes, isLoading } = useReactQueryInvoice()

  if (isLoading) return <Loading />

  const backHandler = () => service.send({ type: Events.BACK })

  const availableProducts = availableProductTypes as ProductName[]

  // Normalize the selection against what the backend actually returned: if the logged-in
  // product has no unpaid invoices, fall back to the first available product so the page
  // does not render an empty state (and, when a single product exists, no tab is shown to fix it).
  const selectedProduct = availableProducts.includes(productFilter)
    ? productFilter
    : (availableProducts[0] ?? productFilter)

  // Client-side filtering by selected product type (consistent with Policies page).
  const filteredInvoices = unpaidInvoices.filter(
    (invoice) => invoice.productType === selectedProduct
  )

  const unpaid = filteredInvoices.map(toInvoiceCardData)

  const body = (
    <>
      <ProductSelect
        handleProductChange={(product: ProductName) => setProductFilter(product)}
        selectedProduct={selectedProduct}
        availableProducts={availableProducts}
      />
      {unpaid.length > 0 ? (
        <>
          <div className={classes.sectionHeader}>
            <Typography className={classes.sectionTitle}>{t('section.unpaid')}</Typography>
          </div>
          <Grid container spacing={2} className={classes.grid}>
            {unpaid.map((invoice) => (
              <Grid key={invoice.id} item xs={12} md={6}>
                <InvoiceCard data={invoice} />
              </Grid>
            ))}
          </Grid>
        </>
      ) : (
        <div className={classes.emptyState}>
          <Typography type='h2'>{t('emptyState.noInvoices')}</Typography>
        </div>
      )}
    </>
  )

  const title = t('title')
  const backBtnText = t('back')

  return (
    <>
      <MobileScreen
        titleHeader={title}
        breadcrumbs
        breadcrumbsBtnText={backBtnText}
        send={backHandler}
      >
        {body}
      </MobileScreen>
      <DesktopScreen breadcrumbs breadcrumbsBtnText={backBtnText} send={backHandler}>
        <Typography type='h1' testId='title' className={classes.title}>
          {title}
        </Typography>
        {body}
      </DesktopScreen>
    </>
  )
}

export default PageInvoicesList
